/*
  JILZARAH — Outlet shopping engine (custom, not part of Flex)

  Progressive enhancement for the outlet landing page. One script serves every
  jilzarah-outlet-* section on the page: the sticky category nav, each collection's
  sentiment selector and package row, the product-detail drawer and the order bar.

  Reusable behaviour lives here; category-specific presentation lives in the sections.
  A future collection with its own art direction only needs to keep the data-jzo-*
  contract below and this script drives it unchanged.

  Contract (rendered by Liquid):
    [data-jzo-product="<variantId>"]   inert JSON per product (snippets/jilzarah-outlet-product)
    [data-jzo-selector]                a pick-quantities-then-add group
      [data-jzo-item="<vid>"]          one item; inside it [data-jzo-qty] (stepper slot) and
                                       [data-jzo-incart] (what is already in the cart)
      [data-jzo-sum]                   "12 necklaces · $83.76"
      [data-jzo-add-selected]          adds only this selector's pending quantities
    [data-jzo-package="<vid>"]         one package with [data-jzo-pack-act] and [data-jzo-incart]
    [data-jzo-detail="<vid>"]          opens the product drawer
    [data-jzo-nav]                     sticky category nav; links carry data-jzo-navlink
                                       (with data-jzo-nav-reveal it stays out of sight until the
                                       page's own category cards, [data-jzo-cats], scroll away;
                                       the cards' links carry data-jzo-navlink too)
    [data-jzo-shell]                   the order bar, the drawer, the toast and the live region

  The cart: pending quantities live on the page until the buyer presses Add. An Add posts
  ONE /cart/add.js request, reads Shopify's answer, then re-reads /cart.js through
  window.JZCart (assets/jilzarah-cart.js) so every "in cart" count and the order bar show
  what is really there. Nothing here reloads the page, moves the scroll position or opens
  the cart drawer on its own; "Review order" opens the theme's own cart drawer, which
  already has editable quantities, remove links and checkout.

  Prices: the product JSON carries a price only for a signed-in customer (Liquid gate).
  Signed out, the sections render a sign-in link instead of controls and this script never
  prints a price.

  Stock: untracked products (the kits) are always orderable and never claimed "in stock";
  tracked ones are capped at the largest valid multiple that fits what is left after the
  cart. If Shopify still refuses, its own message is shown and the selections are kept.
*/
(function () {
  'use strict';
  if (window.JZOutlet) return;

  var CART = null;
  var P = {};            /* variantId -> product JSON */
  var pending = {};      /* variantId -> quantity chosen on the page, not yet in the cart */
  var busy = false;      /* one add at a time */
  var shell = null, bar = null, drawer = null, scrim = null, toastEl = null, liveEl = null;
  var toastT = null, dr = { open: false, vid: null, img: 0 }, lastFocus = null, scrollY = 0;
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- helpers ---------- */
  function money(c) { return '$' + (c / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function say(t) { if (!liveEl) return; liveEl.textContent = ''; setTimeout(function () { liveEl.textContent = t; }, 30); }
  function toast(html, ms, kind) { if (!toastEl) return; toastEl.innerHTML = html; if (kind) toastEl.dataset.kind = kind; else delete toastEl.dataset.kind; /* success | error | low: the stylesheet's state colours and shape */ toastEl.classList.add('on'); clearTimeout(toastT); toastT = setTimeout(function () { toastEl.classList.remove('on'); }, ms || 5000); }
  function root() { var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; return r.replace(/\/?$/, '/'); }
  function plural(n, one, many) { return n === 1 ? one : many; }
  function signedIn() { return Object.keys(P).some(function (k) { return typeof P[k].price === 'number'; }); }
  function inCart(vid) { return CART ? CART.qty(vid) : 0; }

  /* ---------- writing HTML: only when it changed, and never at the cost of focus ----------
     Every render derives the whole page from one truth, and most of it comes out the same as
     the time before. put() skips a write that would change nothing, so a control that did not
     change is not replaced. When it does replace one that held focus, focus goes back to the
     same control (matched by its data-jzo-* attribute), or to the quantity box beside it when
     that control is now disabled (plus at the stock limit, minus at zero).
     For the small slots this engine and its presentation scripts rewrite — steppers, counts,
     the drawer, tab rows — and nothing more. */
  var written = new WeakMap();
  function keyOf(a) {
    if (!a || !a.attributes) return '';
    for (var i = 0; i < a.attributes.length; i++) {
      var at = a.attributes[i];
      if (at.name.indexOf('data-jzo-') === 0) return '[' + at.name + '="' + at.value.replace(/["\\]/g, '\\$&') + '"]';
    }
    return '';
  }
  function refocus(scope, key) {
    var b = scope.querySelector(key);
    if (b && b.disabled) b = (b.closest('[data-jzo-stp]') || scope).querySelector('[data-jzo-in]');
    if (b) b.focus({ preventScroll: true });
  }
  function put(el, html) {
    if (!el || written.get(el) === html) return;
    var a = document.activeElement, key = a && a !== el && el.contains(a) ? keyOf(a) : '';
    el.innerHTML = html; written.set(el, html);
    if (key) refocus(el, key);
  }

  /* ---------- availability: one rule set for every surface ---------- */
  function of(vid) {
    var p = P[vid]; if (!p) return { state: 'hidden', max: 0, label: '' };
    var ov = p.override || 'automatic';
    if (ov === 'hidden') return { state: 'hidden', max: 0, label: '' };
    if (ov === 'temporarily_unavailable' || ov === 'unavailable') return { state: 'unavailable', max: 0, label: 'Temporarily unavailable' };
    if (!p.available && p.tracked) return { state: 'oos', max: 0, label: 'Sold out' };
    if (!p.tracked) return { state: 'untracked', max: Infinity, label: '' };
    if (String(p.policy).toUpperCase() === 'CONTINUE') return { state: 'ok', max: Infinity, label: '' };
    var left = Math.max(0, (p.stock || 0) - inCart(vid));
    var max = Math.floor(left / p.mult) * p.mult;
    var floor = Math.max(p.min, p.mult);
    if (max < floor) return { state: 'oos', max: 0, label: inCart(vid) > 0 ? 'All remaining stock is in your cart' : 'Sold out' };
    if (max <= floor * 5) return { state: 'low', max: max, label: 'Only ' + max + ' left' };
    return { state: 'ok', max: max, label: '' };
  }
  function sellable(vid) { var a = of(vid); return a.state !== 'oos' && a.state !== 'unavailable' && a.state !== 'hidden'; }
  function floorOf(p) { return Math.max(p.min || 1, p.mult || 1); }
  /* snap a typed number to the product's rule, inside what is orderable */
  function normalize(vid, n) {
    var p = P[vid], a = of(vid); if (!p) return 0;
    n = Math.max(0, Math.round(+n || 0));
    if (n === 0) return 0;
    var f = floorOf(p), m = p.mult || 1;
    if (n < f) n = f;
    n = Math.ceil(n / m) * m;
    if (a.max !== Infinity && n > a.max) n = a.max;
    return n;
  }
  function setPending(vid, n) { n = normalize(vid, n); if (n > 0) pending[vid] = n; else delete pending[vid]; }
  function up(vid) { var p = P[vid]; if (!p || !sellable(vid)) return; var q = pending[vid] || 0; setPending(vid, q === 0 ? floorOf(p) : q + p.mult); }
  function down(vid) { var p = P[vid]; if (!p) return; var q = pending[vid] || 0; setPending(vid, q <= floorOf(p) ? 0 : q - p.mult); }

  /* ---------- controls ---------- */
  function stepper(vid, cls) {
    var p = P[vid], q = pending[vid] || 0, a = of(vid);
    var atMax = a.max !== Infinity && q >= a.max;
    return '<div class="jzo-stp' + (cls ? ' ' + cls : '') + '" data-jzo-stp="' + esc(vid) + '">' +
      '<button type="button" data-jzo-dn="' + esc(vid) + '" aria-label="Fewer ' + esc(p.name) + '"' + (q === 0 ? ' disabled' : '') + '>&minus;</button>' +
      '<input type="text" inputmode="numeric" pattern="[0-9]*" value="' + q + '" data-jzo-in="' + esc(vid) + '" aria-label="' + esc(p.name) + ' quantity">' +
      '<button type="button" data-jzo-up="' + esc(vid) + '" aria-label="More ' + esc(p.name) + '"' + (atMax ? ' disabled' : '') + '>+</button></div>';
  }
  function availLine(vid) {
    var a = of(vid); if (!a.label) return '';
    return '<span class="jzo-avail jzo-avail--' + (a.state === 'oos' ? 'oos' : a.state === 'unavailable' ? 'tmp' : 'low') + '">' + esc(a.label) + '</span>';
  }
  function incartLine(vid) { var n = inCart(vid); return n > 0 ? '<span class="jzo-incart__n">' + n + '</span> in your cart' : ''; }

  /* ---------- selectors ---------- */
  function renderItem(item) {
    var vid = item.dataset.jzoItem, p = P[vid]; if (!p) return;
    var slot = item.querySelector('[data-jzo-qty]'), ic = item.querySelector('[data-jzo-incart]');
    var a = of(vid);
    item.classList.toggle('is-unavailable', !sellable(vid));
    item.classList.toggle('is-selected', (pending[vid] || 0) > 0);
    if (slot && signedIn()) {
      if (!sellable(vid)) put(slot, availLine(vid) || '<span class="jzo-avail jzo-avail--oos">Unavailable</span>');
      else put(slot, stepper(vid) + (a.label ? availLine(vid) : ''));
    }
    if (ic) { var t = incartLine(vid); put(ic, t); ic.hidden = !t; }
  }
  function selectorTotals(sel) {
    var t = { count: 0, lines: 0, sub: 0, priced: true };
    sel.querySelectorAll('[data-jzo-item]').forEach(function (it) {
      var vid = it.dataset.jzoItem, q = pending[vid] || 0, p = P[vid];
      if (!(q > 0) || !p) return;
      t.count += q; t.lines++;
      if (typeof p.price === 'number') t.sub += p.price * q; else t.priced = false;
    });
    return t;
  }
  function renderSelector(sel) {
    sel.querySelectorAll('[data-jzo-item]').forEach(renderItem);
    var t = selectorTotals(sel), sum = sel.querySelector('[data-jzo-sum]'), btn = sel.querySelector('[data-jzo-add-selected]');
    var one = sel.dataset.jzoUnit || 'item', many = sel.dataset.jzoUnits || (one + 's');
    if (sum) {
      put(sum, t.count > 0
        ? '<span>' + t.count + ' ' + esc(plural(t.count, one, many)) + '</span>' + (t.priced ? ' <span class="jzo-sum__dot">&middot;</span> <b>' + money(t.sub) + '</b>' : '')
        : '<span class="jzo-sum__empty">' + esc(sel.dataset.jzoEmpty || 'Choose quantities above') + '</span>');
    }
    if (btn) { btn.disabled = t.count === 0 || busy; btn.classList.toggle('is-busy', busy && btn.dataset.jzoBusy === '1'); }
  }
  function renderSelectors() { document.querySelectorAll('[data-jzo-selector]').forEach(renderSelector); }

  /* ---------- packages ---------- */
  function renderPackage(pk) {
    var vid = pk.dataset.jzoPackage, p = P[vid]; if (!p) return;
    var act = pk.querySelector('[data-jzo-pack-act]'), ic = pk.querySelector('[data-jzo-incart]');
    pk.classList.toggle('is-unavailable', !sellable(vid));
    if (act && signedIn()) {
      if (!sellable(vid)) put(act, availLine(vid) || '<span class="jzo-avail jzo-avail--oos">Unavailable</span>');
      else put(act, availLine(vid) + '<button type="button" class="jzo-btn jzo-btn--secondary jzo-btn--wide" data-jzo-add-package="' + esc(vid) + '"' + (busy ? ' disabled' : '') + '>' + esc(act.dataset.jzoLabel || 'Add') + '</button>');
    }
    if (ic) { var t = incartLine(vid); put(ic, t); ic.hidden = !t; }
  }
  function renderPackages() { document.querySelectorAll('[data-jzo-package]').forEach(renderPackage); }

  /* ---------- the cart: one request per Add, then the truth from /cart.js ----------
     addLines resolves ONE result, { state, added, asked, note }, decided by the cart itself:
       'added'     the cart was re-read and grew — by everything asked, or by less (added <
                   asked: a stock cap answers 200 with the capped quantity). This holds whatever
                   the add request answered: Shopify can answer an error AND still have added
                   part of the request ("Only 120 items were added to your cart due to
                   availability"); `note` then carries its reason.
       'failed'    the cart was re-read and did not grow: nothing landed.
       'accepted'  Shopify accepted the add, but the cart could not be re-read: something
                   landed, how much is not known.
       'unknown'   the add got an error or no answer AND the cart could not be re-read.
       'skipped'   another add was still running; nothing was sent.
     A cart that could not be re-read is never guessed at. 'failed', 'accepted' and 'unknown'
     put up their own message here (the same for every caller); the callers word 'added'. */
  var reviewLink = ' <a href="/cart" data-jzo-review>Review order</a>';
  var unverified = false, recheckT = null;
  /* after a failed re-read: read the cart (never add) once more, shortly. JZCart also re-reads
     when the tab regains focus. Whichever read works first notifies the page (boot's CART.on),
     which then brings the theme's header count and cart drawer along too. */
  function recheck() { unverified = true; clearTimeout(recheckT); recheckT = setTimeout(function () { CART.load(); }, 4000); }
  function addLines(lines) {
    if (busy || !lines.length) return Promise.resolve({ state: 'skipped', added: 0, asked: 0, note: '' });
    busy = true; renderAll();
    var before = {}, asked = 0, accepted = false, note = '';
    lines.forEach(function (l) { before[l.id] = inCart(l.id); asked += l.quantity; });
    return fetch(root() + 'cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: lines.map(function (l) { return { id: +l.id, quantity: l.quantity }; }) })
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }, function () { return { ok: false, body: null }; }); })
      .then(function (res) {
        accepted = !!(res.ok && res.body && (res.body.items || res.body.id));
        if (!accepted) { var d = res.body && (res.body.description || res.body.message); note = typeof d === 'string' && d ? d : 'The cart could not be updated.'; }
      }, function () { note = 'We could not reach the cart. Check your connection and try again.'; })
      .then(function () { return CART.load(); })
      .then(function (cart) {
        busy = false;
        var out = { state: 'failed', added: 0, asked: asked, note: note }, msg;
        if (!cart) {
          out.state = accepted ? 'accepted' : 'unknown';
          msg = accepted
            ? "Your add request was accepted, but we couldn't refresh your cart. Review your order to confirm quantities."
            : 'We could not reach the cart. Check your connection, then look at your cart before adding again, in case part of this went through.';
          toast('<b>' + esc(msg) + '</b>' + reviewLink, 9000, accepted ? 'low' : 'error'); say(msg);
          if (accepted) CART.refresh();
          recheck();
          return out;
        }
        lines.forEach(function (l) { out.added += Math.max(0, inCart(l.id) - (before[l.id] || 0)); });
        if (out.added > 0) { out.state = 'added'; CART.refresh(); return out; }
        msg = note || 'That may be all the stock available right now.';
        toast('<b>Not added.</b> ' + esc(msg), 0, 'error'); say('Not added. ' + msg);
        return out;
      });
  }
  /* the confirmation: plain when everything landed, explicit when Shopify capped it */
  function confirmAdd(res, one, many) {
    var added = res.added, short = res.asked > added;
    var t = added + ' ' + plural(added, one, many) + ' added to your cart.';
    if (short) t = res.note ? 'Not everything could be added. ' + res.note : 'Only ' + added + ' of ' + res.asked + ' ' + many + ' could be added. That is all the stock available right now.';
    toast('<b>' + esc(t) + '</b>' + reviewLink, short ? 9000 : 5000, short ? 'low' : 'success'); say(t);
  }
  /* something landed (confirmed, or accepted and not yet confirmed): the picks are spent.
     Anything else keeps them for another try. */
  function landed(res) { return res.state === 'added' || res.state === 'accepted'; }

  function addSelected(sel) {
    var lines = [];
    sel.querySelectorAll('[data-jzo-item]').forEach(function (it) { var vid = it.dataset.jzoItem, q = pending[vid] || 0; if (q > 0 && P[vid]) lines.push({ id: vid, quantity: q }); });
    if (!lines.length) return;
    var btn = sel.querySelector('[data-jzo-add-selected]'); if (btn) btn.dataset.jzoBusy = '1';
    var one = sel.dataset.jzoUnit || 'item', many = sel.dataset.jzoUnits || (one + 's');
    addLines(lines).then(function (res) {
      if (btn) btn.dataset.jzoBusy = '';
      if (landed(res)) lines.forEach(function (l) { delete pending[l.id]; });
      renderAll();
      if (res.state === 'added') confirmAdd(res, one, many);
    });
  }
  function addPackage(vid) {
    var p = P[vid]; if (!p) return;
    addLines([{ id: vid, quantity: 1 }]).then(function (res) {
      renderAll();
      if (res.state !== 'added') return;
      var t = p.name + ' added to your cart.';
      toast('<b>' + esc(t) + '</b>' + reviewLink, 5000, 'success'); say(t);
    });
  }
  function addOne(vid) { /* from the drawer: this item's pending quantity, or its minimum */
    var p = P[vid]; if (!p) return;
    var q = pending[vid] || floorOf(p);
    addLines([{ id: vid, quantity: q }]).then(function (res) {
      if (landed(res)) delete pending[vid];
      renderAll();
      if (res.state === 'added') confirmAdd(res, p.name, p.name);
    });
  }

  /* ---------- order bar ---------- */
  function renderBar(cart) {
    if (!bar) return;
    var n = cart ? cart.item_count : 0;
    bar.hidden = !(n > 0);
    document.body.classList.toggle('jzo-has-bar', n > 0);
    /* so a section's own sticky footer can sit above the bar */
    document.documentElement.style.setProperty('--jzo-bar-h', n > 0 ? bar.offsetHeight + 'px' : '0px');
    if (!(n > 0)) return;
    var c = bar.querySelector('[data-jzo-bar-count]'), t = bar.querySelector('[data-jzo-bar-total]');
    if (c) c.textContent = n + ' ' + plural(n, 'item', 'items') + ' in your order';
    if (t) t.textContent = signedIn() ? money(cart.total_price) : '';
  }

  /* ---------- product drawer ---------- */
  function lock(on) {
    if (on) { scrollY = window.scrollY; document.body.style.top = -scrollY + 'px'; document.body.classList.add('jzo-locked'); }
    else { document.body.classList.remove('jzo-locked'); document.body.style.top = ''; window.scrollTo(0, scrollY); }
  }
  function detailHtml(vid) {
    var p = P[vid]; if (!p) return '';
    var imgs = p.images && p.images.length ? p.images : [{ src: p.image, alt: p.title }];
    var i = Math.min(dr.img, imgs.length - 1), cur = imgs[i] || {};
    var priced = typeof p.price === 'number';
    var control;
    if (!signedIn()) control = '<a class="jzo-btn jzo-btn--primary jzo-btn--wide" href="' + esc(shell.dataset.jzoLogin || '/account/login') + '">Sign in to order</a>';
    else if (!sellable(vid)) control = availLine(vid) || '<span class="jzo-avail jzo-avail--oos">Unavailable</span>';
    else if (p.kind === 'package') control = availLine(vid) + '<button type="button" class="jzo-btn jzo-btn--primary jzo-btn--wide" data-jzo-add-package="' + esc(vid) + '"' + (busy ? ' disabled' : '') + '>Add to cart</button>';
    /* the stepper has its own slot (filled by renderDrawer), so a quantity change rewrites the
       stepper alone and the rest of the drawer — photo, description, scroll position — is left be */
    else control = availLine(vid) + '<div class="jzo-det__ctl"><div class="jzo-det__qty" data-jzo-dr-qty></div><button type="button" class="jzo-btn jzo-btn--primary" data-jzo-add-one="' + esc(vid) + '"' + (busy ? ' disabled' : '') + '>Add to cart</button></div>' +
      '<p class="jzo-det__rule">Sold in multiples of ' + p.mult + '. A quantity chosen here is the same selection shown on the page.</p>';
    var ic = incartLine(vid);
    return '<div class="jzo-det__media"><button type="button" class="jzo-det__zoom" data-jzo-zoom="' + i + '" aria-label="View photo full size"><img src="' + esc(cur.src || p.image) + '" alt="' + esc(cur.alt || p.title) + '" width="900" height="900"></button></div>' +
      (imgs.length > 1 ? '<div class="jzo-det__thumbs">' + imgs.map(function (im, j) { return '<button type="button" data-jzo-img="' + j + '" aria-current="' + (j === i) + '" aria-label="Image ' + (j + 1) + '"><img src="' + esc(im.thumb || im.src) + '" alt="" width="60" height="60"></button>'; }).join('') + '</div>' : '') +
      '<h3 class="jzo-det__title">' + esc(p.title) + '</h3>' +
      '<p class="jzo-det__meta">' + esc(p.sku) + (p.kind === 'package' ? '' : ' &middot; Sold in multiples of ' + p.mult) + '</p>' +
      '<div class="jzo-det__buy">' + (priced ? '<p class="jzo-det__price">' + money(p.price) + (p.kind === 'package' ? '' : ' <span>each</span>') + '</p>' : '') + control +
      (ic ? '<p class="jzo-incart">' + ic + '</p>' : '') + '</div>' +
      (p.descriptionHtml ? '<div class="jzo-det__desc">' + p.descriptionHtml + '</div>' : '') +
      '<p class="jzo-det__view"><a class="jzo-link" href="' + esc(p.url) + '">View product page</a></p>';
  }
  function renderDrawer() {
    if (!dr.open || !drawer) return;
    var body = drawer.querySelector('[data-jzo-dr-body]'), h = drawer.querySelector('[data-jzo-dr-h]'), p = P[dr.vid];
    if (h && p) h.textContent = p.name || p.title;
    if (!body) return;
    /* two writes (the drawer, then its stepper slot), one focus hand-back across both */
    var a = document.activeElement, key = a && body.contains(a) ? keyOf(a) : '';
    put(body, detailHtml(dr.vid));
    put(body.querySelector('[data-jzo-dr-qty]'), stepper(dr.vid, 'jzo-stp--lg'));
    if (key && !body.contains(document.activeElement)) refocus(body, key);
  }
  function openDrawer(vid) {
    if (!drawer || !P[vid]) return;
    var was = dr.open;
    dr.open = true; dr.vid = vid; dr.img = 0;
    if (!was) { lastFocus = document.activeElement; lock(true); drawer.hidden = false; }
    renderDrawer();
    var body = drawer.querySelector('[data-jzo-dr-body]'); if (body) body.scrollTop = 0;
    if (!was) requestAnimationFrame(function () { drawer.classList.add('on'); if (scrim) scrim.classList.add('on'); });
    setTimeout(function () { var c = drawer.querySelector('[data-jzo-close]'); if (c) c.focus(); }, 40);
  }
  /* ---------- full-size view: the drawer's photo, with arrows when there are several ---------- */
  var lb = null, lbState = { open: false, idx: 0 }, lbFocus = null;
  function lbImages() { var p = P[dr.vid]; return p && p.images && p.images.length ? p.images : (p ? [{ src: p.image, zoom: p.image, alt: p.title }] : []); }
  function renderLb() {
    if (!lb || !lbState.open) return;
    var imgs = lbImages(), n = imgs.length; if (!n) return;
    lbState.idx = ((lbState.idx % n) + n) % n;
    var im = imgs[lbState.idx], el = lb.querySelector('[data-jzo-lb-img]');
    if (el) { el.src = im.zoom || im.src; el.alt = im.alt || ''; }
    var prev = lb.querySelector('[data-jzo-lb-prev]'), next = lb.querySelector('[data-jzo-lb-next]'), count = lb.querySelector('[data-jzo-lb-count]');
    if (prev) prev.hidden = n < 2; if (next) next.hidden = n < 2;
    if (count) { count.hidden = n < 2; count.textContent = (lbState.idx + 1) + ' / ' + n; }
    /* the drawer's gallery follows, so closing lands on the photo that was being viewed */
    if (dr.img !== lbState.idx) { dr.img = lbState.idx; renderDrawer(); }
  }
  function openLb(idx) {
    if (!lb || !dr.open) return;
    lbFocus = document.activeElement;
    lbState.open = true; lbState.idx = +idx || 0;
    lb.hidden = false; renderLb();
    requestAnimationFrame(function () { lb.classList.add('on'); });
    setTimeout(function () { var x = lb.querySelector('[data-jzo-lb-close]'); if (x) x.focus(); }, 30);
  }
  function closeLb() {
    if (!lb || !lbState.open) return;
    lbState.open = false; lb.classList.remove('on');
    setTimeout(function () { if (!lbState.open) { lb.hidden = true; var el = lb.querySelector('[data-jzo-lb-img]'); if (el) el.removeAttribute('src'); } }, reduced ? 0 : 200);
    var back = drawer && drawer.querySelector('[data-jzo-zoom]');
    if (back) back.focus(); else if (lbFocus && document.contains(lbFocus)) lbFocus.focus();
  }
  function stepLb(d) { if (!lbState.open) return; lbState.idx += d; renderLb(); }

  function closeDrawer() {
    if (lbState.open) closeLb();
    if (!dr.open) return;
    dr.open = false;
    drawer.classList.remove('on'); if (scrim) scrim.classList.remove('on');
    setTimeout(function () { if (!dr.open) drawer.hidden = true; }, reduced ? 0 : 340);
    lock(false);
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
  }

  /* ---------- sticky category nav ---------- */
  var nav = null, navSpacer = null, navFixed = false, ticking = false, navLinks = [];
  var navReveal = false, revealH = 64, navArrows = null; /* reveal: the strip shows only once the category cards above it have scrolled away */
  function stickyOffset() {
    /* the theme's header is fixed while sticky (jQuery sticky-kit); measure whatever is pinned to the top */
    var off = 0;
    ['#header', '#mobile-header', '.announcement-sticky-wrapper', '.header-sticky-wrapper', '#mobile-header-sticky-wrapper'].forEach(function (s) {
      document.querySelectorAll(s).forEach(function (el) {
        var cs = getComputedStyle(el); if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
        if (cs.visibility === 'hidden' || cs.display === 'none') return;
        var r = el.getBoundingClientRect(); if (r.height === 0 || r.top > 1 || r.width === 0) return;
        off = Math.max(off, r.bottom);
      });
    });
    return Math.round(off);
  }
  function ancestorsAllowSticky(el) {
    for (var a = el.parentElement; a && a !== document.body; a = a.parentElement) {
      var o = getComputedStyle(a).overflow; if (o && o !== 'visible') return false;
    }
    return true;
  }
  function navHeight() { return nav ? nav.offsetHeight : 0; }
  function layoutNav() {
    if (!nav) return;
    var off = stickyOffset();
    document.documentElement.style.setProperty('--jzo-sticky', off + 'px');
    if (navFixed) {
      /* fallback when an ancestor clips: pin with position:fixed and hold the space */
      var top = navSpacer.getBoundingClientRect().top;
      /* a reveal strip appears one strip-height early, so that when it is pinned the content starts right below it */
      var stuck = top <= off + (navReveal ? revealH : 0), was = nav.classList.contains('jzo-nav--stuck');
      nav.classList.toggle('jzo-nav--stuck', stuck);
      nav.style.top = stuck ? off + 'px' : '';
      /* a reveal strip takes no room in the page (it is not there until it is pinned), so nothing is held open for it */
      navSpacer.style.height = stuck && !navReveal ? navHeight() + 'px' : '0px';
      if (navReveal && stuck) revealH = navHeight() || revealH;
      if (stuck !== was && navArrows) navArrows();
    }
    document.documentElement.style.setProperty('--jzo-nav-h', navHeight() + 'px');
  }
  function sectionTargets() {
    return navLinks.map(function (a) { var id = (a.getAttribute('href') || '').replace(/^.*#/, ''); return { a: a, el: id ? document.getElementById(id) : null }; }).filter(function (t) { return visible(t.el); });
  }
  /* ---------- tabs: one category at a time ---------- */
  var tabsMode = false, activeTab = null;
  function tabOf(a) { return (a.getAttribute('href') || '').replace(/^.*#/, ''); }
  function sectionsOfTab(id) {
    return Array.prototype.filter.call(document.querySelectorAll('[data-jzo-collection]:not([data-jzo-fallback])'), function (sec) {
      return sec.id === id || sec.id.indexOf(id + '-') === 0;
    });
  }
  function applyTabs() {
    if (!tabsMode || !nav) return;
    var links = navLinks.filter(function (a) { var li = a.closest('li'); return li && !li.hidden; });
    var ids = links.map(tabOf);
    if (!activeTab || ids.indexOf(activeTab) < 0) activeTab = ids[0] || null;
    navLinks.forEach(function (a) {
      var on = tabOf(a) === activeTab;
      a.classList.toggle('is-active', on); a.setAttribute('aria-selected', String(on)); a.setAttribute('tabindex', on ? '0' : '-1');
      if (on) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
    });
    document.querySelectorAll('[data-jzo-collection]:not([data-jzo-fallback])').forEach(function (sec) {
      var mine = activeTab && (sec.id === activeTab || sec.id.indexOf(activeTab + '-') === 0);
      sec.classList.toggle('jzo-tab-off', !mine);
    });
  }
  function selectTab(id, fromClick) {
    if (!tabsMode) return false;
    var exists = navLinks.some(function (a) { return tabOf(a) === id && a.closest('li') && !a.closest('li').hidden; });
    if (!exists) return false;
    activeTab = id; applyTabs();
    if (history.replaceState) history.replaceState(null, '', '#' + id);
    if (fromClick) {
      /* bring the nav to the top of the viewport when the switch happened further down the page */
      var y = (navSpacer || nav).getBoundingClientRect().top + window.scrollY - stickyOffset();
      if (navReveal) y -= revealH - 1; /* land with the strip still pinned and the category's first row right under it */
      if (window.scrollY > y + 2) window.scrollTo({ top: Math.max(0, y), behavior: reduced ? 'auto' : 'smooth' });
      var sec = sectionsOfTab(id)[0]; if (sec) { if (!sec.hasAttribute('tabindex')) sec.setAttribute('tabindex', '-1'); sec.focus({ preventScroll: true }); }
    }
    return true;
  }

  function spy() {
    if (tabsMode) return;
    var ts = sectionTargets(); if (!ts.length) return;
    var line = stickyOffset() + navHeight() + 12, cur = null;
    ts.forEach(function (t) { if (t.el.getBoundingClientRect().top <= line) cur = t; });
    ts.forEach(function (t) { var on = t === cur; t.a.classList.toggle('is-active', on); if (on) t.a.setAttribute('aria-current', 'true'); else t.a.removeAttribute('aria-current'); });
  }
  function scrollToId(id, focusIt) {
    var el = document.getElementById(id); if (!visible(el)) return false;
    var y = el.getBoundingClientRect().top + window.scrollY - stickyOffset() - navHeight() - 8;
    window.scrollTo({ top: Math.max(0, y), behavior: reduced ? 'auto' : 'smooth' });
    if (focusIt) { if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1'); setTimeout(function () { el.focus({ preventScroll: true }); }, reduced ? 0 : 420); }
    return true;
  }
  function onScroll() { if (ticking) return; ticking = true; requestAnimationFrame(function () { ticking = false; layoutNav(); spy(); }); }
  function initNav() {
    nav = document.querySelector('[data-jzo-nav]'); if (!nav || nav.dataset.jzoReady) return;
    nav.dataset.jzoReady = '1';
    /* the category cards above a reveal strip are the same links: one list, so both follow the active tab */
    var cats = document.querySelector('[data-jzo-cats]');
    navReveal = nav.hasAttribute('data-jzo-nav-reveal');
    navLinks = Array.prototype.slice.call(document.querySelectorAll('[data-jzo-navlink]'));
    tabsMode = nav.dataset.jzoNavMode !== 'scroll';
    if (tabsMode) {
      var h = (location.hash || '').replace(/^#/, '');
      /* #people-we-love-pendants-shop still opens the People We Love tab */
      navLinks.forEach(function (a) { var id = tabOf(a); if (h === id || h.indexOf(id + '-') === 0) activeTab = id; });
      var arrowKeys = function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        var group = e.currentTarget;
        var links = navLinks.filter(function (a) { var li = a.closest('li'); return li && !li.hidden && group.contains(a); });
        var i = links.indexOf(document.activeElement); if (i < 0) return;
        var next = links[(i + (e.key === 'ArrowRight' ? 1 : links.length - 1)) % links.length];
        e.preventDefault(); next.focus(); selectTab(tabOf(next), true);
      };
      nav.addEventListener('keydown', arrowKeys);
      if (cats) cats.addEventListener('keydown', arrowKeys);
    }
    /* development placeholders: hidden for customers, shown with ?outlet_dev=1 (applyVisibility keeps this in step) */
    /* position:sticky cannot leave its containing block, and Shopify wraps every section in a
       div exactly as tall as the section — so the nav is pinned with position:fixed while a
       spacer holds its place. (ancestorsAllowSticky is kept for a future layout where the nav
       lives inside a tall container.) */
    /* setting "Let the site header scroll away": only this bar stays pinned. Flex pins #header /
       #mobile-header with jQuery sticky-kit from its own init (after this script) and re-pins on
       resize only while its enable_sticky flag is true, so: release it, clear the flag, and retry
       for a few seconds in case the theme initialised late. Every other page is untouched. */
    if (nav.hasAttribute('data-jzo-unstick-header')) {
      var tries = 0, unstick = function () {
        var T = window.PXUTheme; tries++;
        if (T) Object.keys(T).forEach(function (k) {
          var o = T[k]; if (!o || typeof o !== 'object' || typeof o.disableSticky !== 'function') return;
          try { if (o.enable_sticky !== false) { o.enable_sticky = false; o.disableSticky(); } } catch (e) { /* theme object not ready yet */ }
        });
        if (document.querySelector('#header.sticky--enabled, #mobile-header.sticky--enabled')) { try { window.jQuery('#header, #mobile-header').unstick().removeClass('sticky--enabled'); } catch (e) {} }
        layoutNav();
        if (tries < 12) setTimeout(unstick, 400);
      };
      if (document.readyState === 'complete') unstick(); else window.addEventListener('load', unstick);
    }
    /* edge arrows: shown only while the category row overflows in that direction (phones with five
       categories); each scrolls the row by most of its width. Also brings the active tab into view. */
    var row = nav.querySelector('.jzo-nav__in'), arrows = nav.querySelectorAll('[data-jzo-nav-arrow]');
    if (row && arrows.length) {
      var arrowState = function () {
        var max = row.scrollWidth - row.clientWidth, x = row.scrollLeft;
        arrows.forEach(function (b) { var dir = +b.dataset.jzoNavArrow; b.hidden = max <= 2 || (dir < 0 ? x <= 2 : x >= max - 2); });
      };
      row.addEventListener('scroll', arrowState, { passive: true }); window.addEventListener('resize', arrowState);
      arrows.forEach(function (b) { b.addEventListener('click', function () { row.scrollBy({ left: +b.dataset.jzoNavArrow * Math.max(120, row.clientWidth * 0.7), behavior: reduced ? 'auto' : 'smooth' }); }); });
      nav.addEventListener('click', function (e) { var a = e.target.closest('[data-jzo-navlink]'); if (a && typeof a.scrollIntoView === 'function') setTimeout(function () { a.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduced ? 'auto' : 'smooth' }); }, 0); });
      navArrows = arrowState; /* a reveal strip cannot be measured until it shows, so layoutNav re-measures then */
      arrowState(); setTimeout(arrowState, 600);
    }
    navFixed = true; nav.classList.add('jzo-nav--fixed');
    navSpacer = document.createElement('div'); navSpacer.className = 'jzo-nav__spacer'; nav.parentNode.insertBefore(navSpacer, nav);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    layoutNav(); spy();
    setTimeout(function () { layoutNav(); spy(); }, 600);
  }

  /* ---------- automatic failure handling: sold out → hidden, empty → section gone, page empty → next tier ----------
     Liquid already leaves sold-out products out of the markup; this mirrors it for anything
     that sells out after the page was rendered, and derives the rest: a selector with no
     items, a package row with no packages, an intro button or photo whose target is gone,
     a section with nothing left, a nav entry with no section, and finally the fallback
     tier(s), of which only the first with stock is shown. */
  function visible(el) { return !!el && !el.hidden && !el.closest('[hidden]'); }
  function soldOut(p) { return !p || p.override === 'hidden' || (p.tracked && !p.available); }
  function applyVisibility() {
    /* start every pass from "everything shown", so a section hidden last time can come back */
    document.querySelectorAll('[data-jzo-collection]').forEach(function (sec) { sec.hidden = false; });
    document.querySelectorAll('[data-jzo-item]').forEach(function (it) { it.hidden = soldOut(P[it.dataset.jzoItem]); });
    document.querySelectorAll('[data-jzo-package]').forEach(function (pk) { pk.hidden = soldOut(P[pk.dataset.jzoPackage]); });
    document.querySelectorAll('[data-jzo-selector]').forEach(function (sel) {
      sel.hidden = !Array.prototype.some.call(sel.querySelectorAll('[data-jzo-item]'), function (it) { return !it.hidden; });
    });
    document.querySelectorAll('.jzo-packages').forEach(function (row) {
      row.hidden = !Array.prototype.some.call(row.querySelectorAll('[data-jzo-package]'), function (pk) { return !pk.hidden; });
    });
    document.querySelectorAll('[data-jzo-for]').forEach(function (el) { el.hidden = !visible(document.getElementById(el.dataset.jzoFor)); });
    /* a section lives while it has a selector, a package, or an intro that still leads somewhere */
    var alive = 0;
    document.querySelectorAll('[data-jzo-collection]').forEach(function (sec) {
      var any = Array.prototype.some.call(sec.querySelectorAll('[data-jzo-selector], .jzo-packages, [data-jzo-for]'), function (el) { return !el.hidden; });
      if (sec.hasAttribute('data-jzo-fallback')) { sec.dataset.jzoStocked = any ? '1' : ''; return; }
      sec.hidden = !any; if (any) alive++;
    });
    var shown = false;
    document.querySelectorAll('[data-jzo-fallback]').forEach(function (sec) {
      var on = !alive && !shown && sec.dataset.jzoStocked === '1';
      sec.classList.toggle('jzo-fallback--on', on); sec.classList.toggle('jzo-fallback--off', !on);
      if (on) shown = true;
    });
    if (nav) {
      var dev = /[?&]outlet_dev=1/.test(location.search) || !!(window.Shopify && window.Shopify.designMode);
      var navAny = false;
      navLinks.forEach(function (a) { /* the strip's pills and, when the page has them, the category cards */
        var li = a.closest('li'); if (!li) return;
        var id = (a.getAttribute('href') || '').replace(/^.*#/, '');
        var isDev = li.hasAttribute('data-jzo-dev');
        li.hidden = isDev ? !dev : !visible(document.getElementById(id));
        if (!li.hidden) navAny = true;
      });
      nav.hidden = !navAny;
      if (navSpacer) navSpacer.hidden = !navAny;
      var cats = document.querySelector('[data-jzo-cats]'); if (cats) cats.hidden = !navAny;
      applyTabs();
    }
  }

  /* ---------- render everything from one truth ---------- */
  function renderAll() {
    renderSelectors(); renderPackages(); applyVisibility(); renderDrawer(); renderBar(CART && CART.get());
    /* presentation scripts (e.g. the jewelry browser) re-derive counts and tiles after every render */
    try { document.dispatchEvent(new CustomEvent('jzo:render')); } catch (e) {}
  }

  /* ---------- events (one delegated listener) ---------- */
  function onClick(e) {
    var b = e.target.closest('button, a'); if (!b) return;
    var d = b.dataset;
    if (d.jzoUp !== undefined) { e.preventDefault(); up(d.jzoUp); renderAll(); say(P[d.jzoUp].name + ': ' + (pending[d.jzoUp] || 0) + ' selected'); return; }
    if (d.jzoDn !== undefined) { e.preventDefault(); down(d.jzoDn); renderAll(); say(P[d.jzoDn].name + ': ' + (pending[d.jzoDn] || 0) + ' selected'); return; }
    if (d.jzoAddSelected !== undefined) { e.preventDefault(); var sel = b.closest('[data-jzo-selector]'); if (sel) addSelected(sel); return; }
    if (d.jzoAddPackage !== undefined) { e.preventDefault(); addPackage(d.jzoAddPackage); return; }
    if (d.jzoAddOne !== undefined) { e.preventDefault(); addOne(d.jzoAddOne); return; }
    if (d.jzoDetail !== undefined) { e.preventDefault(); openDrawer(d.jzoDetail); return; }
    if (d.jzoClose !== undefined) { e.preventDefault(); closeDrawer(); return; }
    if (d.jzoImg !== undefined) { dr.img = +d.jzoImg; renderDrawer(); return; }
    if (d.jzoZoom !== undefined) { e.preventDefault(); openLb(d.jzoZoom); return; }
    if (d.jzoLbClose !== undefined) { e.preventDefault(); closeLb(); return; }
    if (d.jzoLbPrev !== undefined) { e.preventDefault(); stepLb(-1); return; }
    if (d.jzoLbNext !== undefined) { e.preventDefault(); stepLb(1); return; }
    if (d.jzoReview !== undefined) { e.preventDefault(); CART.open(function () { if (dr.open) closeDrawer(); }); return; }
    if (d.jzoScroll !== undefined) { if (scrollToId(d.jzoScroll, true)) e.preventDefault(); return; }
    if (d.jzoNavlink !== undefined) {
      var id = (b.getAttribute('href') || '').replace(/^.*#/, '');
      if (tabsMode) { if (selectTab(id, true)) e.preventDefault(); return; }
      if (scrollToId(id, true)) { e.preventDefault(); if (history.replaceState) history.replaceState(null, '', '#' + id); } return;
    }
  }
  function onInput(e) {
    var inp = e.target; if (!inp.dataset || inp.dataset.jzoIn === undefined) return;
    var vid = inp.dataset.jzoIn;
    if (e.type === 'change' || (e.type === 'keydown' && e.key === 'Enter')) {
      e.preventDefault(); setPending(vid, inp.value); renderAll();
      /* a typed number that snaps back to what was already chosen changes nothing, so nothing
         was rewritten: put the corrected number in the box by hand */
      if (inp.isConnected) inp.value = pending[vid] || 0;
    }
  }
  function onKey(e) {
    if (CART && CART.drawerOpen()) return; /* the theme's cart drawer is on top: its keys are its own */
    if (lbState.open) {
      if (e.key === 'Escape') { e.preventDefault(); closeLb(); return; }
      if (e.key === 'ArrowLeft') { e.preventDefault(); stepLb(-1); return; }
      if (e.key === 'ArrowRight') { e.preventDefault(); stepLb(1); return; }
      if (e.key === 'Tab') {
        var lf = Array.prototype.filter.call(lb.querySelectorAll('button:not([hidden])'), function (x) { return x.offsetParent !== null; });
        if (!lf.length) return;
        var lfirst = lf[0], llast = lf[lf.length - 1];
        if (e.shiftKey && (document.activeElement === lfirst || !lb.contains(document.activeElement))) { e.preventDefault(); llast.focus(); }
        else if (!e.shiftKey && (document.activeElement === llast || !lb.contains(document.activeElement))) { e.preventDefault(); lfirst.focus(); }
      }
      return;
    }
    if (e.key === 'Escape' && dr.open) { closeDrawer(); return; }
    if (e.key === 'Tab' && dr.open && drawer) {
      var f = Array.prototype.filter.call(drawer.querySelectorAll('button:not(:disabled),[href],input:not([disabled]),[tabindex]:not([tabindex="-1"])'), function (x) { return x.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
    }
  }

  /* ---------- boot: gather products and sections (idempotent, editor-safe) ---------- */
  function scan() {
    document.querySelectorAll('[data-jzo-product]').forEach(function (el) {
      if (el.dataset.jzoRead) return; el.dataset.jzoRead = '1';
      var p; try { p = JSON.parse(el.textContent); } catch (e) { return; }
      if (!p || !p.variantId) return;
      p.name = p.name || p.title; P[String(p.variantId)] = p;
    });
    var s = document.querySelector('[data-jzo-shell]');
    if (s && s !== shell) {
      shell = s; bar = s.querySelector('[data-jzo-bar]'); drawer = s.querySelector('[data-jzo-drawer]'); scrim = s.querySelector('[data-jzo-scrim]');
      toastEl = s.querySelector('[data-jzo-toast]'); liveEl = s.querySelector('[data-jzo-live]');
      if (scrim) scrim.addEventListener('click', closeDrawer);
      lb = s.querySelector('[data-jzo-lb]');
      /* a click anywhere that is not the photo or an arrow closes the full-size view */
      if (lb) lb.addEventListener('click', function (e) { if (e.target.closest('[data-jzo-lb-img], [data-jzo-lb-prev], [data-jzo-lb-next]')) return; closeLb(); });
    }
    initNav();
    renderAll();
  }
  function boot() {
    CART = window.JZCart; if (!CART) { setTimeout(boot, 50); return; }
    if (boot.done) { scan(); return; }
    boot.done = true;
    document.addEventListener('click', onClick);
    document.addEventListener('change', onInput);
    document.addEventListener('keydown', onInput);
    document.addEventListener('keydown', onKey);
    CART.on(function () { if (unverified) { unverified = false; CART.refresh(); } renderAll(); });
    CART.onError(function (msg) { toast(esc(msg), 0, 'error'); say(msg); });
    scan();
    CART.load();
    /* arriving with #anchor: in tabs mode the tab is already selected; in scroll mode land below the sticky header and nav */
    if (!tabsMode && location.hash && document.getElementById(location.hash.slice(1))) setTimeout(function () { scrollToId(location.hash.slice(1), false); }, 200);
    window.addEventListener('hashchange', function () { var h = location.hash.replace(/^#/, ''); if (tabsMode) { navLinks.forEach(function (a) { var id = tabOf(a); if (h === id || h.indexOf(id + '-') === 0) selectTab(id, false); }); } });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', function () { setTimeout(boot, 0); });

  window.JZOutlet = { pending: pending, products: P, scan: scan, open: openDrawer, close: closeDrawer, scrollTo: scrollToId, soldOut: function (vid) { return soldOut(P[String(vid)]); }, render: renderAll, put: put };
})();
